/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {AddPopUpComponent} from '../../../core/components/pop-ups/add-pop-up/add-pop-up.component';
import {StateService} from '../../../core/state/state.service';
import {SegmentSuffix} from '../../../models/segment-suffix.model';
import {SegmentSuffixService} from '../../../services/segment-suffix.service';


@Component({
    selector: 'app-segment-suffix-management',
    templateUrl: './segment-suffix-management.component.html',
    styleUrl: './segment-suffix-management.component.scss',
    standalone: false
})
export class SegmentSuffixManagementComponent implements OnInit {

  @Output() spinnerEvent = new EventEmitter<boolean>();
  @Output() dataChangeEvent = new EventEmitter<void>();
  suffixes: SegmentSuffix[];

  constructor(readonly segmentSuffixService: SegmentSuffixService,
              public snackBar: FabMessageService,
              public state: StateService,
              public dialog: MatDialog) {
  }


  ngOnInit(): void {
    this.getAllSuffixes();
  }

  /**
   * Retrieves all segment suffixes from the database
   */
  getAllSuffixes(): void {
    this.spinnerEvent.emit(true);
    this.segmentSuffixService.getAllSuffixes().subscribe(
      data => {
        this.suffixes = data;
      },
      (err) => {
        this.spinnerEvent.emit(false);
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      () => {
        this.spinnerEvent.emit(false);
      }
    );
  }

  /**
   * Opens the pop-up to add a new segment suffix
   * maxLength = 4 because the DB allows max segment length 25
   * and longest segment name is 20, accounting for the '-<suffix>'
   */
  openAddPopUp(): void {
    const dialogRef = this.dialog.open(AddPopUpComponent, {
      data: {
        title: 'Create Suffix',
        maxLength: 4,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.createSuffix(res.data.newValue);
      }
    });
  }

  /**
   * Creates a new segment suffix, adding child segments to existing level 1 and 2 segments,
   * and creating mappings for already mapped level 1 and 2 segments
   */
  createSuffix(suffix: string): void {
    this.spinnerEvent.emit(true);
    this.segmentSuffixService.createSuffix(this.state.getUserGmid(), suffix).subscribe({
        error: (err) => {
          this.spinnerEvent.emit(false);
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        complete: () => {
          this.spinnerEvent.emit(false);
          this.dataChangeEvent.emit();
          this.snackBar.displaySuccess('Successfully created suffix', DISMISS_MSG);
          this.getAllSuffixes();
        }
      }
    );
  }

  /**
   * Deletes a segment suffix, reallocating all volumes to ND-<suffix> and deleting all mappings
   * @param suffix The suffix to delete
   */
  deleteSuffix(suffix: SegmentSuffix): void {
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: 'Selected suffix and all Propulsion level segments attached to it will be deleted.' +
          ' Those with volumes will be reallocated to ND-' + suffix.segmentSuffix + '. Are you sure?',
        noLabel: 'Cancel',
        yesLabel: 'Confirm',
        titleIcon: 'warning',
        actionColor: 'warn'
      },
      disableClose: true
    })
    .afterClosed().subscribe({
      next: confirm => {
        if (confirm) {
          this.spinnerEvent.emit(true);
          this.segmentSuffixService.deleteSuffix(suffix.segmentSuffix).subscribe({
            error: (err) => {
              this.spinnerEvent.emit(false);
              this.snackBar.displayError(err, DISMISS_MSG);
            },
            complete: () => {
              this.spinnerEvent.emit(false);
              this.dataChangeEvent.emit();
              this.snackBar.displaySuccess('Successfully deleted suffix', DISMISS_MSG);
              this.getAllSuffixes();
            }
          });
        }
      }
    });
  }
}
